import {
  UseMutateAsyncFunction,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query'
import axios, { AxiosError, AxiosResponse } from 'axios'
import { useEffect, useMemo } from 'react'
import {
  AppointmentNoteAddenda as AppointmentNoteAddendaRoute,
  AppointmentNotes as AppointmentNotesRoute,
  SaveAppointmentNotes as SaveAppointmentNotesRoute,
  SaveNoteForPatient as SaveNoteForPatientRoute,
} from '../api/routes'
import { QUERY_KEYS } from '../api/querykeys'
import { AppointmentNote, AppointmentNoteAddendum } from '../types/types'
import { AddendumOpInput } from '../utils/appointment-note-addendum.utils'
import { useSourcePluginContext } from '../context/SourcePluginContext'
import { useAlertContext } from '../context/AlertContext'
import produce from 'immer'

interface SaveNoteDto {
  note: AppointmentNote
  files?: ArrayLike<File>[]
}

interface CreateAddendumDto {
  files?: ArrayLike<File>[]
  noteId: string
  ops: AddendumOpInput[]
  comment?: string
}

export interface CreateAddendumResponse {
  noop: boolean
  addendum: AppointmentNoteAddendum | null
  effective: Partial<AppointmentNote>
  note: AppointmentNote
}

interface UseAppointmentNotesInterface {
  isLoading: boolean
  error: unknown
  notes: AppointmentNote[]
  handleSaveNote: UseMutateAsyncFunction<
    AxiosResponse<string> | null,
    unknown,
    SaveNoteDto,
    unknown
  >
  handleSaveNoteForPatient: UseMutateAsyncFunction<
    AxiosResponse<{ text: string }> | null,
    unknown,
    {
      noteId: string
      noteForPatient: string
    },
    unknown
  >
  handleCreateAddendum: UseMutateAsyncFunction<
    AxiosResponse<CreateAddendumResponse>,
    unknown,
    CreateAddendumDto,
    unknown
  >
}

const useAppointmentNotes = (member?: string): UseAppointmentNotesInterface => {
  const queryClient = useQueryClient()
  const { setAlertText } = useAlertContext()
  const { getAuthHeader } = useSourcePluginContext()

  const fetchAppointmentNotes = async (): Promise<AppointmentNote[]> => {
    if (!member) {
      return []
    }

    const authHeader = await getAuthHeader()
    return fetch(
      `${process.env.REACT_APP_SERVER_URL}${AppointmentNotesRoute(member)}`,
      { headers: authHeader }
    )
      .then(async (res) => {
        const data = await res.json()

        if (res.ok) {
          return data
        } else if (res.status === 403) {
          setAlertText('Unauthorized - you may need to reload the page')
          return []
        } else {
          setAlertText(`Error loading appointment notes (${res.statusText})`)
          return []
        }
      })
      .catch(() => {
        setAlertText(`Error loading appointment notes`)
        return []
      })
  }

  const { isLoading, error, data } = useQuery(
    [QUERY_KEYS.APPOINTMENT_NOTES, member ?? 'default'],
    fetchAppointmentNotes
  )

  const notes = useMemo(() => data ?? [], [data])

  const updateNote = async ({ note, files }: SaveNoteDto) => {
    const authHeader = await getAuthHeader()

    const formData = new FormData()

    if (files) {
      files.forEach((fileList) => {
        Array.from(fileList).forEach((file) => {
          formData.append('files', file)
        })
      })
    }

    formData.append('note', JSON.stringify(note))

    return axios
      .put(
        `${process.env.REACT_APP_SERVER_URL}${SaveAppointmentNotesRoute(
          note.id
        )}`,
        formData,
        { headers: authHeader }
      )
      .catch((err) => {
        const typedErr = err as AxiosError
        const errMessage = typedErr.response?.data
          ? typedErr.response.data.message
          : typedErr.message
        setAlertText(`Unable to save note.\n\n${errMessage}`)
        return null
      })
  }

  const mutateUpdateNote = useMutation(updateNote, {
    onSettled: () =>
      queryClient.invalidateQueries(QUERY_KEYS.APPOINTMENT_NOTES),
    onMutate: async ({ note, files }: SaveNoteDto) => {
      await queryClient.cancelQueries(QUERY_KEYS.APPOINTMENT_NOTES)

      const previousData = queryClient.getQueryData<AppointmentNote[]>(
        QUERY_KEYS.APPOINTMENT_NOTES
      )

      if (previousData) {
        const nextState = produce(previousData, (draft) => {
          const index = draft.findIndex((note) => note.id === note.id)
          draft[index] = {
            ...note,
            attachments: files?.length
              ? files.flatMap((fileList) =>
                  Array.from(fileList).map((file, i) => ({
                    id: String(i),
                    fileId: String(i),
                    fileName: file.name,
                  }))
                )
              : undefined,
          }
        })
        queryClient.setQueryData<AppointmentNote[]>(
          QUERY_KEYS.APPOINTMENT_NOTES,
          nextState
        )
      }

      return { previousData }
    },
  })

  /**
   * Special method that allows updating only the "Note for patient" field ('text' column in Allara DB)
   * within an already signed note. This is an exception to the normal flow where signed notes
   * are immutable.
   *
   * Note: This is a provider-only utility that silently updates the note content. It does not
   * trigger any notifications to the patient about the changes made to their note.
   *
   * @param noteId - The ID of the signed note to update (UUID v4 format e.g. '123e4567-e89b-12d3-a456-426614174000')
   * @param noteForPatient - The new text for the "Note for patient".
   */
  const saveNoteForPatient = async ({
    noteId,
    noteForPatient,
  }: {
    noteId: string
    noteForPatient: string
  }) => {
    const authHeader = await getAuthHeader()

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}${SaveNoteForPatientRoute(noteId)}`,
        { text: noteForPatient },
        { headers: authHeader }
      )
      return response
    } catch (err) {
      const typedErr = err as AxiosError
      const errMessage = typedErr.response?.data
        ? typedErr.response.data.message
        : typedErr.message
      setAlertText(`Unable to update note for patient.\n\n${errMessage}`)
      throw err
    }
  }

  /**
   * Mutation to update the patient-facing note text of a signed note (the note for patient).
   *
   * Features:
   * - Optimistic updates: UI updates immediately before server confirmation
   * - Automatic rollback: If the server request fails, changes are reverted
   * - Cache invalidation: Refreshes note data after successful update
   */

  const mutateSaveNoteForPatient = useMutation(saveNoteForPatient, {
    // A signed note's text save is routed through the addendum path, so it
    // writes history too and the history query has to know.
    onSettled: () => {
      queryClient.invalidateQueries(QUERY_KEYS.APPOINTMENT_NOTES)
      queryClient.invalidateQueries(QUERY_KEYS.APPOINTMENT_NOTE_ADDENDA)
    },

    onMutate: async ({ noteId, noteForPatient }) => {
      await queryClient.cancelQueries(QUERY_KEYS.APPOINTMENT_NOTES)
      const previousNotes = queryClient.getQueryData<AppointmentNote[]>(
        QUERY_KEYS.APPOINTMENT_NOTES
      )

      if (previousNotes) {
        const nextState = produce(previousNotes, (draft) => {
          const noteIndex = draft.findIndex((note) => note.id === noteId)

          if (noteIndex !== -1) {
            // Update the text field since that's what's used in the UI
            draft[noteIndex].text = noteForPatient
          }
        })
        queryClient.setQueryData<AppointmentNote[]>(
          QUERY_KEYS.APPOINTMENT_NOTES,
          nextState
        )
      }

      return { previousNotes }
    },
    onError: (err, newNote, context) => {
      if (context?.previousNotes) {
        // On failure restore the previous notes
        queryClient.setQueryData(
          QUERY_KEYS.APPOINTMENT_NOTES,
          context.previousNotes
        )
      }
    },
  })

  /**
   * Appends an addendum to an already signed note. The server recomputes the note
   * from the ops and can return a no-op, so there is no optimistic update here.
   */
  const createAddendum = async ({
    noteId,
    ops,
    comment,
    files,
  }: CreateAddendumDto) => {
    const authHeader = await getAuthHeader()

    const body = new FormData()
    body.append('ops', JSON.stringify(ops))

    if (comment) {
      body.append('comment', comment)
    }

    files?.forEach((list) =>
      Array.from(list).forEach((file) => body.append('files', file))
    )

    try {
      return await axios.post<CreateAddendumResponse>(
        `${process.env.REACT_APP_SERVER_URL}${AppointmentNoteAddendaRoute(
          noteId
        )}`,
        body,
        { headers: authHeader }
      )
    } catch (err) {
      const typedErr = err as AxiosError
      const errMessage = typedErr.response?.data
        ? typedErr.response.data.message
        : typedErr.message
      setAlertText(`Unable to amend note.\n\n${errMessage}`)
      throw err
    }
  }

  const mutateCreateAddendum = useMutation(createAddendum, {
    // The edit history is its own query, so the notes list alone leaves the
    // entry the provider just wrote off screen until a reload.
    onSettled: () => {
      queryClient.invalidateQueries(QUERY_KEYS.APPOINTMENT_NOTES)
      queryClient.invalidateQueries(QUERY_KEYS.APPOINTMENT_NOTE_ADDENDA)
    },
  })

  useEffect(() => {
    queryClient.invalidateQueries(QUERY_KEYS.APPOINTMENT_NOTES)
  }, [member])

  return {
    isLoading,
    error,
    notes,
    handleSaveNote: mutateUpdateNote.mutateAsync,
    handleSaveNoteForPatient: mutateSaveNoteForPatient.mutateAsync,
    handleCreateAddendum: mutateCreateAddendum.mutateAsync,
  }
}

export default useAppointmentNotes
